import { getUserRbac, initSignup, managementLogin, updateUserProfile, validateToken } from "@/api/authentication";
import {
	GeneralRoleEnum,
	InitSignupPayload,
	InitSignupResponse,
	LoginManagementPayload,
	PermissionEnum,
	ResultLoginManagement,
	UpdateUserProfileRequest,
	UpdateUserResponse,
	UserRbacResponse,
} from "@/modules/authentication";
import { useAuthenticationContext } from "@/modules/authentication/context/useAuthenticationContext";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useInitSignup = () => {
	return useMutation<InitSignupResponse, Error, InitSignupPayload>({
		mutationFn: (payload) => {
			return initSignup(payload);
		},
	});
};

export const useToken = (enabled: boolean) => {
	return useQuery({
		queryKey: ["userProfile"],
		queryFn: () => validateToken(),
		staleTime: 0,
		enabled,
	});
};

export const useLoginManagement = () => {
	return useMutation<ResultLoginManagement, Error, LoginManagementPayload>({
		mutationFn: (payload) => {
			return managementLogin(payload);
		},
	});
};

export const useUpdateUserProfile = () => {
	return useMutation<UpdateUserResponse, Error, UpdateUserProfileRequest>({
		mutationFn: (payload) => {
			return updateUserProfile(payload);
		},
	});
};

export const useUserRbac = (userId: number | undefined, enabled: boolean) => {
	return useQuery<UserRbacResponse>({
		queryKey: ["userRbac", userId],
		queryFn: () => getUserRbac(userId!),
		enabled: enabled && !!userId,
		staleTime: 0,
	});
};

export const useHasPermission = (permission: PermissionEnum | PermissionEnum[]): boolean => {
	const { rbac } = useAuthenticationContext();
	if (rbac?.roleSlug === GeneralRoleEnum.SUPER_ADMIN) {
		return true;
	}

	const permissions = rbac?.permissions ?? [];
	const required = Array.isArray(permission) ? permission : [permission];
	return required.every((p) => permissions.includes(p));
};
